import { useUpdateEffect } from 'ahooks';
import { Form } from 'antd';
import { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { CommonDrawerTitle, FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  DomainConfigFormFields,
  DomainConfigFormInputs,
  DomainConfigurationStatus,
  DomainDeploymentInfo,
  DomainsQueryKeys,
  useGetConfigDomainsQuery,
  useUpdateConfigDomainsMutation,
} from '@/modules/Domains';

import { DomainsConfigFormView } from './DomainConfigFormView';
import { domainConfigFormValidationSchema } from './validation';

interface Props {
  domainId: string;
}

export const DomainsConfigEditFormContainer: FC<Props> = memo(({ domainId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const [form] = Form.useForm<DomainConfigFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainConfigFormInputs>>(null);

  const proxyServerIdFormValue = Form.useWatch(DomainConfigFormInputs.ProxyServerId, form) as string | undefined;
  const dnsRecordIdFormValue = Form.useWatch(DomainConfigFormInputs.DnsRecordId, form) as string | undefined;

  const {
    data: domainDeploymentInfoData,
    error: domainDeploymentInfoError,
    isLoading: isDomainDeploymentInfoLoading,
    isError: isDomainDeploymentInfoError,
    isSuccess: isDomainDeploymentInfoSuccess,
  } = useGetConfigDomainsQuery(domainId)({
    enabled: !!domainId,
  });

  const { mutate: updateConfigMutation, isLoading: setupDeploymentConfigIsLoading } = useUpdateConfigDomainsMutation(domainId)({
    onSuccess: () => {
      if (isDomainDeploymentInfoSuccess) {
        queryClient.setQueryData<DomainDeploymentInfo>([DomainsQueryKeys.GetConfig, domainId], (data) => {
          return {
            ...data,
            configurationStatus: DomainConfigurationStatus.Pending,
          } as DomainDeploymentInfo;
        });
      }

      handleBack();
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof DomainConfigFormInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  useUpdateEffect(() => {
    if (domainDeploymentInfoData?.configInfo) {
      form.setFieldsValue({
        isHttps: domainDeploymentInfoData.configInfo.isHttps,
        siteId: domainDeploymentInfoData.configInfo.site.id,
        dnsRecordId: domainDeploymentInfoData.configInfo.dnsRecord.id,
        proxyServerAddressId: domainDeploymentInfoData.configInfo.proxyServerAddress.id,
        proxyServerId: domainDeploymentInfoData.configInfo.proxyServer.id,
        wwwRedirect: domainDeploymentInfoData.configInfo.wwwRedirect,
        schemeRedirect: domainDeploymentInfoData.configInfo.schemeRedirect,
        staticPrefix: domainDeploymentInfoData.configInfo.staticPrefix,
      });
    }
  }, [domainDeploymentInfoData, form]);

  const initialValues: DomainConfigFormFields = {
    isHttps: domainDeploymentInfoData?.configInfo?.isHttps,
    siteId: domainDeploymentInfoData?.configInfo?.site.id,
    dnsRecordId: domainDeploymentInfoData?.configInfo?.dnsRecord.id,
    proxyServerAddressId: domainDeploymentInfoData?.configInfo?.proxyServerAddress.id,
    proxyServerId: domainDeploymentInfoData?.configInfo?.proxyServer.id,
    wwwRedirect: domainDeploymentInfoData?.configInfo?.wwwRedirect,
    schemeRedirect: domainDeploymentInfoData?.configInfo?.schemeRedirect,
    staticPrefix: domainDeploymentInfoData?.configInfo?.staticPrefix,
  };

  const handleFinish = useCallback((values: DomainConfigFormFields): void => {
    setFormErrors(null);
    updateConfigMutation({ ...values, staticPrefix: values.staticPrefix || undefined });
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    form.resetFields();
    navigate(AppRoutes.DomainsById.replace(':domainId', domainId));
  }, [form, domainId]);

  return (
    <FallbackedLayout
      data={domainDeploymentInfoData}
      isLoading={isDomainDeploymentInfoLoading}
      isError={isDomainDeploymentInfoError}
      error={domainDeploymentInfoError}
    >
      {(domainDeploymentInfo): ReactElement => (
        <>
          <CommonDrawerTitle>{domainDeploymentInfo.name}</CommonDrawerTitle>

          <DomainsConfigFormView
            formName="domain-configuration-update"
            formType="edit"
            form={form}
            formErrors={formErrors}
            initialValues={initialValues}
            isFormLoading={setupDeploymentConfigIsLoading}
            validationSchema={domainConfigFormValidationSchema}
            proxyServerIdFormValue={proxyServerIdFormValue as string}
            dnsRecordIdFormValue={dnsRecordIdFormValue as string}
            handleBack={handleBack}
            handleFinish={handleFinish}
            handleFinishFailed={handleFinishFailed}
          />
        </>
      )}
    </FallbackedLayout>
  );
});
